import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineEditAltIcon from 'LuxKit/Icons/line/LineEditAltIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { rem } from 'polished'
import styled from 'styled-components'
import { LabRemoteConfigKeys } from 'constants/lab'
import { useLabRemoteConfig } from 'hooks/FeatureFlag/useLabRemoteConfig'
import useExperience from 'hooks/Experiences/useExperience'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isRecord } from 'lib/object/objectUtils'
import LoyaltyEsimUpsellModal from 'loyalty/components/LoyaltyEsimUpsellModal'
import { applyEsimUpsellCheckoutSelection } from 'loyalty/thunks/applyEsimUpsellCheckoutSelection'
import { startPostPurchaseEsimCheckoutSelection } from 'loyalty/thunks/startPostPurchaseEsimCheckoutSelection'
import type { LoyaltyEsimUpsellModalResult } from 'loyalty/components/LoyaltyEsimUpsellModal'
import { getLoyaltyEsimUpsellCartSelection } from 'loyalty/lib/esims/esimCheckoutUtils'
import { formatEsimPackageLabel } from 'loyalty/utils/loyaltyEsimLabels'
import useEsimUpsellCatalog from 'loyalty/hooks/useEsimUpsellCatalog'
import type { LoyaltyOrderEsimIncludedBenefit } from 'loyalty/hooks/useLoyaltyOrderViewEsimData'

const LayoutContainer = styled.div`
  container-type: inline-size;
  max-width: 100%;
  width: 100%;
`

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: ${rem(6)};
  column-gap: ${rem(12)};
  max-width: 100%;
  min-width: 0;

  @container (min-width: ${rem(250)}) {
    flex-direction: row;
    align-items: center;
  }
`

const RowLabel = styled(BodyText)`
  min-width: 0;
  max-width: 100%;
`

const DataChip = styled(AssistChip)`
  flex: 0 0 auto;
  max-width: 100%;
`

interface LoyaltyEsimAdditionalDataRowUpsell {
  currentPackageTicketId: App.LoyaltyEsimUpsellTicketId
  /** When provided, skips the catalog fetch and defers visibility to the parent. */
  catalog?: App.LoyaltyEsimUpsellCatalog
}

interface Props {
  includedBenefit: LoyaltyOrderEsimIncludedBenefit
  countryCode: string
  /** Existing order, when the row is rendered inside checkout or a My Escapes order view. */
  order?: App.Order
  upsell: LoyaltyEsimAdditionalDataRowUpsell
  /**
   * Checkout flow: modal selections are written to the cart, the chip reflects cart state,
   * and reopening the modal pre-selects the cart item. Order-view flow leaves the cart alone
   * and routes paid selections into post-purchase checkout instead.
   */
  checkoutMode?: boolean
}

function LoyaltyEsimAdditionalDataRow({
  includedBenefit,
  countryCode,
  order,
  upsell,
  checkoutMode = false,
}: Props) {
  const { currentPackageTicketId, catalog: initialCatalog } = upsell
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const payload = useLabRemoteConfig(LabRemoteConfigKeys.loyaltyEsimUpsell)
  const isEsimUpsellEnabled = useMemo(() => {
    const { value } = payload ?? {}
    return isRecord(value) && value.enabled === true
  }, [payload])

  const cartSelection = useAppSelector((state) => (
    checkoutMode ? getLoyaltyEsimUpsellCartSelection(state.checkout.cart.items) : undefined
  ))
  const selectedPackageTicketId = cartSelection?.selectedPackageTicketId ?? null

  const { catalog: fetchedCatalog, isLoading, hasPurchasableUpgrades } = useEsimUpsellCatalog({
    countryCode,
    orderId: order?.id,
    currentPackageTicketId,
    enabled: !initialCatalog,
  })
  const catalog = initialCatalog ?? fetchedCatalog
  const [experience, fetchingExperience, experienceError] = useExperience(
    catalog?.esimOfferId ?? '',
    { disabled: !catalog?.esimOfferId },
  )
  const hasCartUpsell = checkoutMode && !!selectedPackageTicketId
  const isExperienceReady = !!experience && !fetchingExperience && !experienceError
  const fallbackGbLabel = formatEsimPackageLabel(intl, currentPackageTicketId)
  const selectedLabel = checkoutMode && selectedPackageTicketId
    ? formatEsimPackageLabel(intl, selectedPackageTicketId)
    : undefined
  const chipGbLabel = selectedLabel ?? fallbackGbLabel

  const rowLabel = intl.formatMessage({
    id: 'tGvyy0M2fPJzYulY0CBi',
    defaultMessage: 'Add additional data',
    description: 'Label beside the action to open the eSIM data upgrade modal from the benefit tile',
  })

  const onOpenUpgrade = useCallback(async() => {
    if (!catalog) return

    // Defensive, already handled by rendering null until these are present and ready
    if (!isExperienceReady && !hasCartUpsell) return

    const result = await showModal<LoyaltyEsimUpsellModalResult | void>(
      <LoyaltyEsimUpsellModal
        catalog={catalog}
        countryCode={countryCode}
        experience={experience}
        includedBenefit={includedBenefit}
        currentPackageTicketId={currentPackageTicketId}
        checkoutMode={checkoutMode}
      />,
    )
    if (!result) return
    if (checkoutMode) {
      await dispatch(applyEsimUpsellCheckoutSelection(result))
      return
    }
    if (order) {
      await dispatch(startPostPurchaseEsimCheckoutSelection(result, order))
      return
    }
    showSnackbar(
      intl.formatMessage({
        id: 'K08Q6qcRfJv6C8VMU_wP',
        defaultMessage: 'Data add-ons can be added from checkout before you complete your booking.',
        description: 'Snackbar when user selects a paid eSIM upgrade from order view (cart change not supported)',
      }),
      'neutral',
    )
  }, [catalog, checkoutMode, countryCode, currentPackageTicketId, dispatch, experience, hasCartUpsell, includedBenefit, intl, isExperienceReady, order, showModal])

  if (!isEsimUpsellEnabled) {
    return null
  }

  if (!initialCatalog && (isLoading || !hasPurchasableUpgrades)) {
    return null
  }

  if (!catalog || (!isExperienceReady && !hasCartUpsell)) {
    return null
  }

  return (
    <LayoutContainer>
      <Layout>
        <RowLabel variant="medium" weight="bold">
          {rowLabel}
        </RowLabel>
        <DataChip
          type="button"
          variant="outline"
          size="small"
          endIcon={<LineEditAltIcon size="S" />}
          onClick={onOpenUpgrade}
        >
          {chipGbLabel}
        </DataChip>
      </Layout>
    </LayoutContainer>
  )
}

export default LoyaltyEsimAdditionalDataRow
